import './index.css'

import get from 'lodash/get'
import lodashMaxBy from 'lodash/maxBy'
import uniqBy from 'lodash/uniqBy'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  openPlaylistById,
  openTrackRoute,
} from '@disco/state-management/actions/ExplorerActions'
import * as NotificationActions from '@disco/state-management/actions/NotificationActions'
import { updateUserSettings } from '@disco/state-management/actions/UserActions'

import type {
  BriefSubmissionNotification,
  MentionNotification,
  ReactionNotification,
} from 'types/notification'
import type { User } from 'types/user'

import enhanceWithClickOutside from 'utils/enhance-with-click-outside'

import { getDiscussionMixpanelProperties } from './get-discussion-mixpanel-properties'
import NotificationPane from '../../notification-pane'
import NotificationBell from '../../notification-pane/notification-bell'

const mapStateToProps = state => ({
  user: state.user,
  notifications: state.notifications,
})

const mapDispatchToProps = {
  ...NotificationActions,
  updateUserSettings,
  sendMixpanelEvent,
  openTrackRoute,
  openPlaylistById,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PlaylistId = number
type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  navigate: NavigateFunction
}
type State = {
  selectedNotification: PlaylistId | null
}

class HeaderNotifications extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      // TODO: move this.state.selectedNotification to reducer
      selectedNotification: null,
    }
  }

  // Called by the click-outside HOC.
  handleClickOutside = evt => {
    // Prevent closing Notifications when clicking on inner tooltip
    let target = evt.target

    while (target) {
      if (target.className === 'ToolTipPortal') return
      target = target.parentNode
    }

    const { notifications } = this.props
    const { isNotificationPaneVisible } = notifications
    if (!isNotificationPaneVisible) return

    this._handleToggle()
  }

  _getSelectedNotification(
    selectedNotification,
  ): Record<string, any> | null | undefined {
    const { unreadNotifications } = this.props.notifications
    return unreadNotifications.find(
      notification => notification.playlist_id === selectedNotification,
    )
  }

  _handleToggle = () => {
    this.setState({
      selectedNotification: null,
    })
    this.props.toggleNotificationsPane()
  }
  _handleNotificationBellClick = () => {
    this._handleToggle()

    const { isNotificationPaneVisible } = this.props.notifications
    if (isNotificationPaneVisible) return null
    this.props.updateUserSettings({
      settings: {
        counter_timestamp: new Date().toISOString(),
      },
    })
  }
  _handleExpand = () => {
    this.props.expandNotificationsPane()
  }
  _handleClear = () => {
    this.props.dismissAllNotifications()
  }
  _handleOpenNotification = notification => {
    this.props.openNotification()
    this.props.toggleNotificationsPane()
  }
  _handleDismiss = notification => {
    this.props.dismissNotification(notification)
  }
  _handleCollapseNotification = () => {
    this.setState({
      selectedNotification: null,
    })
  }
  _handleExpandNotification = notification => {
    this.props.navigate(`/explorer/playlists/${notification.playlist_id}/stats`)
    this.setState({
      selectedNotification: notification.playlist_id,
    })
    this.props.sendMixpanelEvent('Open notification details')
    this.props.toggleNotificationsPane()
  }
  _handleOpenPlaylist = notification => {
    this._handleToggle()

    const { playlist_id, group_id } = notification
    this.props.openPlaylistById(playlist_id, group_id)
    this.props.sendMixpanelEvent('Access playlist stats via notifications')
  }

  _handleOpenBriefSubmission = (notification: BriefSubmissionNotification) => {
    this._handleToggle()

    this.props.navigate(
      `/briefs/${notification.brief_id}/submissions/${notification.playlist_id}`,
    )
    this.props.sendMixpanelEvent('Access brief submission via notifications')
  }

  _handleAcceptChannelInvite = notification => {
    this._handleToggle()

    const { channel_id } = notification
    this.props.navigate(`/explorer/channels/${channel_id}/join`)
    this.props.sendMixpanelEvent('Accept channel invite via notifications')
  }
  _handleOpenTrackRoute = notification => {
    this._handleToggle()

    let trackId = notification.track.id

    if (!trackId) {
      const url = notification.track.app_url
      trackId = parseInt(new URL(url).pathname.split('/').pop() ?? '')
      if (isNaN(trackId)) throw new Error('Cannot parse track url ' + url)
    }

    this.props.openTrackRoute(trackId)
  }
  _handleOpenLibraryClients = notification => {
    this._handleToggle()

    this.props.navigate(`/catalogs/${notification.client_library_id}/clients`)
    this.props.sendMixpanelEvent('Access library clients via notification')
  }
  _handleOpenDiscussion = (
    notification: MentionNotification | ReactionNotification,
  ) => {
    this._handleToggle()

    this.props.navigate({
      pathname: `/explorer/tracks/${notification.track_id}/edit-metadata/`,
      search: `?tab=notes&note=${notification.discussion_id}${
        notification.visibility ? `&visibility=${notification.visibility}` : ''
      }`,
    })

    this.props.sendMixpanelEvent(
      'Open discussion via notification',
      getDiscussionMixpanelProperties(notification),
    )
  }

  _getTimeOfLastEvent(list: Record<string, any>[]) {
    if (list.length === 0) return null
    return lodashMaxBy(list, 'timestamp')?.timestamp
  }

  _getAggregatedNotificationPane(
    notification: Record<string, any> | null | undefined,
  ) {
    const nonEmpty: Record<string, any>[] = []
    if (!notification) return nonEmpty
    const { api_url } = notification

    if (notification.playlist_downloads.length) {
      nonEmpty.push({
        ...notification,
        timestamp: this._getTimeOfLastEvent(notification.playlist_downloads),
        type: 'aggregated_stats_playlist_downloads',
        api_url: api_url + '-p-d',
      })
    }

    if (notification.track_downloads.length) {
      nonEmpty.push({
        ...notification,
        timestamp: this._getTimeOfLastEvent(notification.track_downloads),
        type: 'aggregated_stats_track_downloads',
        api_url: api_url + '-t-d',
      })
    }

    if (notification.track_streams.length) {
      nonEmpty.push({
        ...notification,
        timestamp: this._getTimeOfLastEvent(notification.track_streams),
        type: 'aggregated_stats_track_streams',
        api_url: api_url + '-t-s',
      })
    }

    if (notification.playlist_accesses.length) {
      nonEmpty.push({
        ...notification,
        timestamp: this._getTimeOfLastEvent(notification.playlist_accesses),
        type: 'aggregated_stats_playlist_accesses',
        api_url: api_url + '-p-a',
      })
    }

    return nonEmpty
  }

  _getNotificationsToShow(unreadNotifications) {
    const { selectedNotification } = this.state

    if (selectedNotification) {
      return this._getAggregatedNotificationPane(
        this._getSelectedNotification(selectedNotification),
      )
    } else {
      return uniqBy(unreadNotifications, 'api_url')
    }
  }

  _getBadgeCounterForNotifications(notifications) {
    const counter_timestamp = get(
      this.props.user,
      'settings.counter_timestamp',
      null,
    )
    if (!counter_timestamp) return notifications.length
    const counter_timestamp_date = new Date(counter_timestamp)
    const matching = notifications.filter(notification => {
      return new Date(notification.timestamp) > counter_timestamp_date
    })
    return matching.length
  }

  render() {
    const { notifications } = this.props

    const notificationsToShow = this._getNotificationsToShow(
      notifications.unreadNotifications,
    )

    return (
      <div className="HeaderNotifications hidden-phone">
        <NotificationBell
          badge={this._getBadgeCounterForNotifications(notificationsToShow)}
          onClick={this._handleNotificationBellClick}
        />
        {this._renderPane(notificationsToShow)}
      </div>
    )
  }

  _renderPane(aggregatedNotifications) {
    const { notifications } = this.props
    const { isNotificationPaneVisible } = notifications
    return (
      <div
        className={`HeaderNotifications_PaneDropdown ${
          isNotificationPaneVisible
            ? 'HeaderNotifications_PaneDropdown--visible'
            : 'HeaderNotifications_PaneDropdown--hidden'
        }`}
      >
        <NotificationPane
          notifications={aggregatedNotifications}
          hasScrollBar
          hasDismissButton
          badge={this._getBadgeCounterForNotifications(aggregatedNotifications)}
          selectedNotification={this._getSelectedNotification(
            this.state.selectedNotification,
          )}
          push={this.props.navigate}
          onOpenNotification={this._handleOpenNotification}
          onDismiss={this._handleDismiss}
          onShowAll={this._handleExpand}
          onCollapseNotification={this._handleCollapseNotification}
          onExpandNotification={this._handleExpandNotification}
          onClearAll={this._handleClear}
          onOpenPlaylist={this._handleOpenPlaylist}
          onOpenBriefSubmission={this._handleOpenBriefSubmission}
          onOpenDiscussion={this._handleOpenDiscussion}
          onAcceptChannelInvite={this._handleAcceptChannelInvite}
          onOpenLibraryClients={this._handleOpenLibraryClients}
          onOpenTrackRoute={this._handleOpenTrackRoute}
          onClickLink={this._handleToggle}
        />
      </div>
    )
  }
}

const ConnectedHeaderNotifications = connector(
  enhanceWithClickOutside(HeaderNotifications),
)

export default function HeaderNotificationsWithRouter(props: any) {
  const navigate = useNavigate()
  return <ConnectedHeaderNotifications {...props} navigate={navigate} />
}
